import * as React from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { skipToken } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Hook specifically for handling the Shopify integration connection
 * *during* the onboarding flow. It checks for a pendingAppInstallationId or shopifyIntegrationId
 * search parameter and attempts to connect it to the user's newly created project.
 */
export default function useConnectShopifyIntegrationOnboarding({
  targetPath,
}: {
  targetPath: string;
}) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();
  const { openModal } = useModal();
  const [searchParams] = useSearchParams();
  const pendingAppInstallationId = searchParams.get("pendingAppInstallationId");
  const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");

  const handleSuccess = () => {
    void trpcUtils.project.listWithStats.invalidate();
    void trpcUtils.workspace.getById.invalidate();

    if (user) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "setup",
        stepType: "setup",
        stepData: {
          shopifyIntegrationId,
          pendingAppInstallationId,
        },
      });
    }

    navigate(targetPath!);
  };
  const {
    mutate: connectProjectToPendingInstallation,
    isPending: isConnectingProjectToPendingInstallation,
    isError: isProjectPendingConnectionError,
  } = trpc.project.connectToPendingInstallation.useMutation({
    onSuccess: () => handleSuccess(),
  });
  const {
    mutate: connectProjectToExistingIntegration,
    isPending: isConnectingProjectToIntegration,
    isError: isProjectIntegrationConnectionError,
  } = trpc.project.connectToIntegration.useMutation({
    onSuccess: () => handleSuccess(),
  });

  const {
    data: integrationDataFromParams,
    isLoading: isIntegrationDataLoading,
  } = trpc.integration.get.useQuery(
    shopifyIntegrationId ? { id: shopifyIntegrationId } : skipToken,
  );
  const { data: userWorkspaces, isFetching: isUserWorkspacesDataLoading } =
    trpc.workspace.getUserWorkspacesList.useQuery();
  const { data: userProjects, isFetching: isUserProjectsLoading } =
    trpc.project.listWithStats.useQuery({});

  const workspaceId = userWorkspaces?.workspaces[0]?.id;
  const integrationOwningWorkspaceId =
    integrationDataFromParams?.integration.workspaceId;
  const integrationOwningWorkspace = userWorkspaces?.workspaces.find(
    ({ id }) => id === integrationOwningWorkspaceId,
  );
  const projectId = userProjects?.projects[0]?.id;

  const isMutationLoading =
    isConnectingProjectToPendingInstallation ||
    isConnectingProjectToIntegration;
  const isMutationError =
    isProjectPendingConnectionError || isProjectIntegrationConnectionError;
  const isQueryLoading =
    isIntegrationDataLoading ||
    isUserWorkspacesDataLoading ||
    isUserProjectsLoading;

  React.useEffect(() => {
    // Note (Noah, 2025-02-25): If we have no pending app installation id AND no shopify integration id
    // in the params, then we didn't just come from Shopify. Just do nothing.
    if (!pendingAppInstallationId && !shopifyIntegrationId) {
      return;
    }

    // Note (Noah, 2025-02-25): If we're still loading then we might not have determined what to do in the
    // automatic post-onboarding case. Just return because we'll re-run this effect once we have the data
    if (isMutationLoading || isMutationError || isQueryLoading) {
      return;
    }

    if (
      !isUserWorkspacesDataLoading &&
      !isUserProjectsLoading &&
      !workspaceId &&
      !projectId
    ) {
      return;
    }

    // Note (Noah, 2025-02-25): Now we know that we came from Shopify, because we have a query param
    // indicating it.

    // NOTE (Matt 2024-09-26): If there is a pendingAppInstallationId, and a pendingIntegrationAutoConnectTarget
    // we should automatically connect the corresponding project or workspace to the pending install.
    if (pendingAppInstallationId) {
      if (projectId) {
        connectProjectToPendingInstallation({
          projectId,
          shopify: { pendingAppInstallationId },
        });
      } else {
        navigate(targetPath!);
      }
      return;
    } else if (shopifyIntegrationId) {
      if (integrationOwningWorkspaceId === workspaceId) {
        if (projectId) {
          connectProjectToExistingIntegration({
            projectId,
            integrationId: shopifyIntegrationId,
          });
        } else {
          navigate(targetPath!);
        }
      } else {
        // Note (Noah, 2025-02-25): The referenced Shopify integration is connected to a workspace
        // which is DIFFERENT from the one we came to Shopify from. So we need to show a message -
        // either that the user is not a member of that workspace, or that the workspace does not
        // own the integration and they should disconnect/reconnect if they want to associate it
        // with a different workspace.
        const isUserMemberOfIntegrationOwningWorkspace = Boolean(
          integrationOwningWorkspace,
        );
        if (!isUserMemberOfIntegrationOwningWorkspace) {
          // NOTE (Matt 2024-09-26): At this point, this means the user has attempted to add a shopify store to Replo, but that Shopify Store has been installed
          // by a workspace the user does not belong to. We explain this error and tell them to reach out to support.
          openModal({
            type: "fullPageErrorModal",
            props: {
              details: {
                header: "Shopify Store already installed",
                message:
                  "This Shopify Store is already installed on another workspace that you do not have access to. In order to install this Shopify Store on your workspace, get access from the workspace admin, or contact support@replo.app for help.",
                callToAction: {
                  type: "closeModal",
                  name: "Close",
                },
              },
            },
          });
        } else {
          // NOTE (Matt 2024-09-26): If the user is a member of the workspace that owns the integration, but they have a cookie whose
          // workspaceId does not match that of the workspace that owns the integration, we need to inform them that what they are
          // trying to do won't work, and link them to the integrations hub for the workspace that owns the integration.
          openModal({
            type: "fullPageErrorModal",
            props: {
              details: {
                header: "Shopify Store already installed",
                message: `This Shopify Integration is already installed on another workspace that you belong to. In order to install this Shopify Integration on a different workspace, you will first need to uninstall it from "${integrationOwningWorkspace!.name}".`,
                isFullPage: true,
                callToAction: {
                  type: "closeModal",
                  name: "Close",
                },
              },
            },
          });
        }
      }
      searchParams.delete("shopifyIntegrationId");
    }
  }, [
    workspaceId,
    projectId,
    isUserWorkspacesDataLoading,
    isUserProjectsLoading,
    pendingAppInstallationId,
    shopifyIntegrationId,
    isMutationLoading,
    isMutationError,
    isQueryLoading,
    integrationOwningWorkspaceId,
    integrationOwningWorkspace,
    connectProjectToPendingInstallation,
    connectProjectToExistingIntegration,
    openModal,
    navigate,
    targetPath,
    searchParams,
  ]);

  return {
    isLoading: isMutationLoading || isQueryLoading,
    isError: isMutationError,
    isChecking: isUserWorkspacesDataLoading || isUserProjectsLoading,
    foundPendingInstall: Boolean(pendingAppInstallationId),
  };
}
